import React, { useEffect, useState } from "react"

import {
  Row,
  TableBodyStyled,
  TableCellStyled,
  TableContainerStyled,
  TableFooter,
  TableHeadStyled,
  TableRowStyled,
  TableStyled
} from "components"
import { DeleteIcon, ViewFileIcon } from "components/Icons"
import { useTabs, useLoading, useModal, useException } from "hooks"
import { IHandleChangePage, IHandleChangeRowsPerPage } from "hooks/useGet"
import { UseWorkAccidentOccurrence } from "pages/GST/Registers/WorkAccidentOccurrence/context"
import { IPaginatedEntityGST, IResponseTypes } from "services/core/genericEntityGST"
import { IOcorrenciasByFilterItens, WorkAccidentOccurrenceServices } from "services/GST/WorkAccidentOccurrence"
import { FORMAT } from "utils"

interface NewTabProps {
  tabName: string
  createAndNavigateToNewTab?: boolean
}

interface IDocumentTableProps {
  refreshTable: () => void
  tableData: IPaginatedEntityGST<IOcorrenciasByFilterItens[]>
  page: number,
  rowsPerPage: number,
  handleChangePage: IHandleChangePage
  handleChangeRowsPerPage: IHandleChangeRowsPerPage
}

const AccidentTable: React.FC<IDocumentTableProps> = ({
  handleChangePage,
  handleChangeRowsPerPage,
  page,
  rowsPerPage,
  tableData,
  refreshTable
}) => {
  const {
    handleAddTab,
    tabs,
    handleRemoveTab,
    rawTabs,
  } = useTabs()
  const {
    handleSetIdOcorrenciaSelecionado,
  } = UseWorkAccidentOccurrence()
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const { getException, createCustomException } = useException()

  const [selectedIdOcorrencia, setSelectedIdOcorrencia] = useState<string>('')

  const handleNewTab = (tabProps: NewTabProps) => {
    if (tabs.length > 2) {
      handleRemoveTab({
        removeAndNavigateToPreviousDefaultTab: false,
        tabName: rawTabs[2].name
      })
      setSelectedIdOcorrencia(tabProps.tabName)
    } else {
      handleAddTab(tabProps)
    }
  }

  const handleDeleteOcorrencia = (idOcorrencia: number) => {
    setLoading('loading')

    WorkAccidentOccurrenceServices()
      .deleteOcorrencia({
        idEntity: idOcorrencia,
      })
      .then(response => {
        if (response?.status !== 200 || response?.data?.tipo === IResponseTypes['alert']) {
          const { tipo, detalhe, mensagem, } = response?.data

          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setLoading('idle')
        refreshTable()
        showModal({
          hideButtonCancel: true,
          message: 'Ocorrência foi deletada com sucesso!',
          txtButtonConfirm: 'OK',
          type: 'success',
        })
      })
      .catch((e: Error) => {
        setLoading('idle')
        const error = getException(e)

        showModal({
          type: error.tipo || 'error',
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
        })
      })
  }

  useEffect(() => {
    if (selectedIdOcorrencia) {
      handleNewTab({
        createAndNavigateToNewTab: true,
        tabName: selectedIdOcorrencia
      })
    }
  }, [tabs])

  return (
    <>
      <TableContainerStyled>
        <TableStyled>
          <TableHeadStyled>
            <TableRowStyled>
              <TableCellStyled align="center" minwidth="20px">Mês/Ano</TableCellStyled>
              <TableCellStyled align="center" minwidth="20px">Empresa</TableCellStyled>
              <TableCellStyled align="center" minwidth="20px">Centro</TableCellStyled>
              <TableCellStyled align="center" minwidth="50px">Tipo</TableCellStyled>
              <TableCellStyled align="center" minwidth="50px">CPF/CNPJ/Cód.Fornecedor</TableCellStyled>
              <TableCellStyled align="center" minwidth="50px">Fornecedor</TableCellStyled>
              <TableCellStyled align="center" minwidth="50px">Ações</TableCellStyled>
            </TableRowStyled>
          </TableHeadStyled>
          <TableBodyStyled>
            {tableData?.entity?.items?.length > 0 &&
              tableData?.entity?.items?.map((row, index) => (
                <TableRowStyled key={index}>
                  <TableCellStyled align="center">{FORMAT.competencia(row?.nuAnoMesCompetencia) || '---'}</TableCellStyled>
                  <TableCellStyled align="center">{row?.txEmpresa || '---'}</TableCellStyled>
                  <TableCellStyled align="center">{row?.txCentro || '---'}</TableCellStyled>
                  <TableCellStyled align="center">{row?.nmTipo || '---'}</TableCellStyled>
                  <TableCellStyled align="center">{FORMAT.formatCdDocto(row?.cdCpfCnpj) || '---'}</TableCellStyled>
                  <TableCellStyled align="center">{row?.nmFornecedor || '---'}</TableCellStyled>
                  <TableCellStyled align="center">
                    <Row justifyContent="center" alignItems="center">
                      <ViewFileIcon
                        cursor="pointer"
                        onClick={() => {
                          handleSetIdOcorrenciaSelecionado(row?.idOcorrencia) // set no Provider
                          handleNewTab({
                            createAndNavigateToNewTab: true,
                            tabName: `${FORMAT.competencia(row?.nuAnoMesCompetencia)} - ${row?.nmFornecedor}`,
                          })
                        }}
                      />
                      <DeleteIcon
                        cursor="pointer"
                        onClick={() => {
                          showModal({
                            type: 'alert',
                            titleModal: 'Atenção!',
                            message: 'Tem certeza de que deseja deletar a ocorrência?',
                            txtButtonConfirm: 'SIM',
                            txtButtonCancel: 'Cancelar',
                            handleConfirm: () => handleDeleteOcorrencia(row?.idOcorrencia)
                          })
                        }} />
                    </Row>
                  </TableCellStyled>
                </TableRowStyled>
              ))}
          </TableBodyStyled>
        </TableStyled>
      </TableContainerStyled>
      <TableFooter
        page={page}
        rows={tableData?.entity?.items}
        typeFooter="online"
        totalItems={tableData?.entity?.pageCount}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  )
}

export default AccidentTable
